import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import DoctorMascot from './components/DoctorMascot';
import { ToastProvider } from './contexts/ToastContext';
import { AgencyAuthProvider } from './contexts/AgencyAuthContext';
import { ApplicantAuthProvider } from './contexts/ApplicantAuthContext';
import { MarketingAuthProvider } from './contexts/MarketingAuthContext';
import { PrincipalAuthProvider } from './contexts/PrincipalAuthContext';
import { InquiryAuthProvider } from './contexts/InquiryAuthContext';
import { InformationAuthProvider } from './contexts/InformationAuthContext';
import { TransmittalAuthProvider } from './contexts/TransmittalAuthContext';
import { getMaintenanceStatus } from './services/maintenance';

const Home = lazy(() => import('./pages/Home'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Services = lazy(() => import('./pages/Services'));
const Branches = lazy(() => import('./pages/Branches'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PreRegistrationWrapper = lazy(() => import('./components/PreRegistrationWrapper'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserLogin = lazy(() => import('./pages/UserLogin'));
const AgencyDashboard = lazy(() => import('./pages/AgencyDashboard'));
const AgencyChangePassword = lazy(() => import('./pages/AgencyChangePassword'));
const ApplicantLogin = lazy(() => import('./pages/ApplicantLogin'));
const ApplicantDashboard = lazy(() => import('./pages/ApplicantDashboard'));
const PrincipalDashboard = lazy(() => import('./pages/PrincipalDashboard'));
const MarketingDashboard = lazy(() => import('./pages/MarketingDashboard'));
const InquiryDashboard = lazy(() => import('./pages/InquiryDashboard'));
const InformationDashboard = lazy(() => import('./pages/InformationDashboard'));
const TransmittalDashboard = lazy(() => import('./pages/TransmittalDashboard'));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'));
const AgencyRouteGuard = lazy(() => import('./components/AgencyRouteGuard'));
const ApplicantRouteGuard = lazy(() => import('./components/ApplicantRouteGuard'));
const MarketingRouteGuard = lazy(() => import('./components/MarketingRouteGuard'));
const PrincipalRouteGuard = lazy(() => import('./components/PrincipalRouteGuard'));
const InquiryRouteGuard = lazy(() => import('./components/InquiryRouteGuard'));
const InformationRouteGuard = lazy(() => import('./components/InformationRouteGuard'));
const TransmittalRouteGuard = lazy(() => import('./components/TransmittalRouteGuard'));
const TurnstileVerification = lazy(() => import('./components/TurnstileVerification'));

type PublicMaintenanceGuardProps = {
  pageKey: string;
  children: React.ReactElement;
};

const PublicMaintenanceGuard = ({ pageKey, children }: PublicMaintenanceGuardProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkMaintenanceStatus = async () => {
      const status = await getMaintenanceStatus(pageKey);

      if (!isMounted) {
        return;
      }

      setIsUnderMaintenance(Boolean(status?.is_active));
      setIsLoading(false);
    };

    checkMaintenanceStatus();

    return () => {
      isMounted = false;
    };
  }, [pageKey]);

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isUnderMaintenance) {
    return <Navigate to={`/maintenance/${pageKey}`} replace />;
  }

  return children;
};

function App() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <ToastProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Suspense
            fallback={
              <div className="min-h-[40vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            }
          >
            <Routes>
              {/* Admin routes without header/footer */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/pdsci-admin" element={<AdminLogin />} />
              <Route path="/pdsci-admin/dashboard" element={<AdminDashboard />} />

              {/* User login for agency, principal, marketing, inquiry, information */}
              <Route path="/agency/login" element={<UserLogin />} />
              <Route path="/user/login" element={<UserLogin />} />

              {/* Public maintenance route */}
              <Route path="/maintenance/:pageKey" element={<MaintenancePage />} />

              {/* Agency routes - only dashboard and change-password, login is now generic */}
              <Route path="/agency/*" element={
                <AgencyAuthProvider>
                  <Routes>
                    <Route path="change-password" element={<AgencyChangePassword />} />
                    <Route path="dashboard" element={
                      <AgencyRouteGuard>
                        <AgencyDashboard />
                      </AgencyRouteGuard>
                    } />
                  </Routes>
                </AgencyAuthProvider>
              } />

              {/* Applicant routes - Wrap all applicant routes in single provider */}
              <Route path="/applicant/*" element={
                <ApplicantAuthProvider>
                  <Routes>
                    <Route path="login" element={<ApplicantLogin />} />
                    <Route path="dashboard" element={
                      <ApplicantRouteGuard>
                        <ApplicantDashboard />
                      </ApplicantRouteGuard>
                    } />
                  </Routes>
                </ApplicantAuthProvider>
              } />

              {/* Principal routes */}
              <Route path="/principal/*" element={
                <PrincipalAuthProvider>
                  <Routes>
                    <Route path="dashboard" element={
                      <PrincipalRouteGuard>
                        <PrincipalDashboard />
                      </PrincipalRouteGuard>
                    } />
                  </Routes>
                </PrincipalAuthProvider>
              } />

              {/* Marketing routes */}
              <Route path="/marketing/*" element={
                <MarketingAuthProvider>
                  <Routes>
                    <Route path="dashboard" element={
                      <MarketingRouteGuard>
                        <MarketingDashboard />
                      </MarketingRouteGuard>
                    } />
                  </Routes>
                </MarketingAuthProvider>
              } />

              {/* Inquiry routes */}
              <Route path="/inquiry/*" element={
                <InquiryAuthProvider>
                  <Routes>
                    <Route path="dashboard" element={
                      <InquiryRouteGuard>
                        <InquiryDashboard />
                      </InquiryRouteGuard>
                    } />
                  </Routes>
                </InquiryAuthProvider>
              } />

              {/* Information routes */}
              <Route path="/information/*" element={
                <InformationAuthProvider>
                  <Routes>
                    <Route path="dashboard" element={
                      <InformationRouteGuard>
                        <InformationDashboard />
                      </InformationRouteGuard>
                    } />
                  </Routes>
                </InformationAuthProvider>
              } />

              {/* Transmittal routes */}
              <Route path="/transmittal/*" element={
                <TransmittalAuthProvider>
                  <Routes>
                    <Route path="dashboard" element={
                      <TransmittalRouteGuard>
                        <TransmittalDashboard />
                      </TransmittalRouteGuard>
                    } />
                  </Routes>
                </TransmittalAuthProvider>
              } />

              {/* Pre-registration routes - Verification first, then form */}
              <Route
                path="/pre-registration"
                element={
                  <PublicMaintenanceGuard pageKey="pre-registration">
                    <TurnstileVerification />
                  </PublicMaintenanceGuard>
                }
              />
              <Route
                path="/pre-registration/form"
                element={
                  <PublicMaintenanceGuard pageKey="pre-registration">
                    <PreRegistrationWrapper />
                  </PublicMaintenanceGuard>
                }
              />

              {/* Main site routes with header/footer */}
              <Route path="/*" element={
                <>
                  <Header />
                  <DoctorMascot />
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <PublicMaintenanceGuard pageKey="home">
                          <Home />
                        </PublicMaintenanceGuard>
                      }
                    />
                    <Route
                      path="/about-us"
                      element={
                        <PublicMaintenanceGuard pageKey="about-us">
                          <AboutUs />
                        </PublicMaintenanceGuard>
                      }
                    />
                    <Route
                      path="/services"
                      element={
                        <PublicMaintenanceGuard pageKey="services">
                          <Services />
                        </PublicMaintenanceGuard>
                      }
                    />
                    <Route
                      path="/branches"
                      element={
                        <PublicMaintenanceGuard pageKey="branches">
                          <Branches />
                        </PublicMaintenanceGuard>
                      }
                    />
                    <Route
                      path="/contact-us"
                      element={
                        <PublicMaintenanceGuard pageKey="contact-us">
                          <ContactUs />
                        </PublicMaintenanceGuard>
                      }
                    />
                  </Routes>
                  <Footer />
                </>
              } />
            </Routes>
          </Suspense>

          {/* Back to Top Button */}
          {showBackToTop && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-8 right-8 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 z-50"
              aria-label="Back to top"
            >
              <ChevronUp className="h-6 w-6" />
            </button>
          )}
        </div>
      </Router>
    </ToastProvider>
  );
}

export default App;
